import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useApi, useApiMutation } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { QRScanner } from "@/components/shared/QRScanner";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { showToast } from "@/components/shared/Toast";
import {
  Key,
  Eye,
  EyeOff,
  Trash2,
  QrCode,
  Save,
  ShieldCheck,
  User,
  Palmtree,
  Calendar,
  X,
  Bell,
  KeyRound,
} from "lucide-react";
import { subscribeToPush, resubscribeToPush, sendSubscriptionToServer } from "@/lib/pwa";
import {
  getClientKey,
  setClientKey,
  removeClientKey,
  isValidEncryptionKey,
} from "@/lib/crypto";

interface HolidayRequest {
  starts_at: string;
  ends_at: string;
  status: "pending" | "approved" | "denied";
  requested_at: string;
}

interface ClientProfileData {
  _key: string;
  name: string;
  icon: string;
  description: string;
  active: boolean;
  startedAt: string | null;
  endsAt: string | null;
  startedBy: string | null;
  adminHoliday: boolean;
  adminEndsAt?: string | null;
  request: HolidayRequest | null;
}

export function ClientProfile() {
  const { jwt } = useParams();
  const { user, getHeaders, setAuth } = useAuth();

  const { data: profile, loading, error, refetch } = useApi<ClientProfileData>(
    `/api/client/${jwt}/profile`,
  );

  // Use _key from profile or user for localStorage key
  const clientId = profile?._key || user?._key || "";

  // Encryption key state
  const [encKey, setEncKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [showScanner, setShowScanner] = useState(false);

  // Holiday request state
  const [holidayStartDate, setHolidayStartDate] = useState("");
  const [holidayEndDate, setHolidayEndDate] = useState("");
  const [holidayLoading, setHolidayLoading] = useState(false);

  // Notification state
  const [notificationStatus, setNotificationStatus] = useState<"granted" | "denied" | "default" | "unsupported">("unsupported");
  const [notificationLoading, setNotificationLoading] = useState(false);

  // Passcode change state
  const [currentPasscode, setCurrentPasscode] = useState("");
  const [newPasscode, setNewPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");
  const [passcodeLoading, setPasscodeLoading] = useState(false);

  useEffect(() => {
    if (clientId) {
      setEncKey(getClientKey(clientId));
    }
  }, [clientId]);

  useEffect(() => {
    if (typeof Notification !== "undefined") {
      setNotificationStatus(Notification.permission);
    }
  }, []);

  const handleImportKey = () => {
    const trimmed = keyInput.trim();
    if (!trimmed) {
      showToast.error("Please enter an encryption key");
      return;
    }
    if (!isValidEncryptionKey(trimmed)) {
      showToast.error("Invalid encryption key format");
      return;
    }
    setClientKey(clientId, trimmed);
    setEncKey(trimmed);
    setKeyInput("");
    showToast.success("Encryption key imported successfully");
  };

  const handleScanResult = (data: string) => {
    setShowScanner(false);
    const trimmed = data.trim();
    if (!isValidEncryptionKey(trimmed)) {
      showToast.error("Scanned QR code does not contain a valid encryption key");
      return;
    }
    setClientKey(clientId, trimmed);
    setEncKey(trimmed);
    showToast.success("Encryption key imported from QR code");
  };

  const handleClearKey = () => {
    removeClientKey(clientId);
    setEncKey(null);
    showToast.success("Encryption key removed");
  };

  const handleRequestHoliday = async () => {
    if (!holidayStartDate || !holidayEndDate) {
      showToast.error("Please select both start and end dates");
      return;
    }
    setHolidayLoading(true);
    try {
      const res = await fetch(`/api/client/${jwt}/holiday-request`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({
          starts_at: new Date(holidayStartDate).toISOString(),
          ends_at: new Date(holidayEndDate).toISOString(),
        }),
      });
      if (res.ok) {
        showToast.success("Holiday request submitted for admin approval");
        setHolidayStartDate("");
        setHolidayEndDate("");
        refetch();
      } else {
        const data = await res.json().catch(() => ({}));
        showToast.error(data.error || "Failed to submit request");
      }
    } catch {
      showToast.error("Failed to submit request");
    } finally {
      setHolidayLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    setHolidayLoading(true);
    try {
      const res = await fetch(`/api/client/${jwt}/holiday-request`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (res.ok) {
        showToast.success("Holiday request cancelled");
        refetch();
      }
    } catch {
      showToast.error("Failed to cancel request");
    } finally {
      setHolidayLoading(false);
    }
  };

  const handleChangePasscode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPasscode || !newPasscode) {
      showToast.error("Please fill in all passcode fields");
      return;
    }
    if (newPasscode.length < 4) {
      showToast.error("New passcode must be at least 4 digits");
      return;
    }
    if (newPasscode !== confirmPasscode) {
      showToast.error("New passcodes do not match");
      return;
    }
    setPasscodeLoading(true);
    try {
      const res = await fetch(`/api/client/${jwt}/change-passcode`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({
          current_passcode: currentPasscode,
          new_passcode: newPasscode,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      // Update stored passcode in auth state
      setAuth({ passcode: newPasscode });
      setCurrentPasscode("");
      setNewPasscode("");
      setConfirmPasscode("");
      showToast.success("Passcode changed successfully");
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed to change passcode");
    } finally {
      setPasscodeLoading(false);
    }
  };

  const handleEnableNotifications = async (forceReregister = false) => {
    setNotificationLoading(true);
    try {
      // Fetch VAPID public key
      const vapidResp = await fetch("/api/push/vapid-public-key");
      if (!vapidResp.ok) {
        showToast.error("Failed to get push configuration");
        return;
      }
      const { publicKey } = await vapidResp.json();

      // Subscribe to push notifications
      const subscription = forceReregister
        ? await resubscribeToPush(publicKey)
        : await subscribeToPush(publicKey);
      if (!subscription) {
        if (Notification.permission === "granted") {
          showToast.error("Push notifications aren't supported by this browser. Try Chrome instead.");
        } else {
          showToast.error("Notification permission denied or unavailable");
        }
        setNotificationStatus(Notification.permission);
        return;
      }

      // Send subscription to server
      const success = await sendSubscriptionToServer(
        subscription,
        "client",
        jwt!,
        getHeaders(),
      );

      if (success) {
        showToast.success(forceReregister ? "Notifications re-registered!" : "Notifications enabled successfully!");
        setNotificationStatus("granted");
      } else {
        showToast.error("Failed to save notification subscription");
      }
    } catch (err) {
      showToast.error("Failed to enable notifications");
      console.error(err);
    } finally {
      setNotificationLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;
  if (!profile) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Profile Header */}
      <Card className="border-0 shadow-lg overflow-hidden py-0">
        <div className="bg-linear-to-br from-app-primary/20 via-app-accent/10 to-transparent p-6 md:p-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-app-surface-raised shadow-md flex items-center justify-center">
              <span className="text-3xl md:text-4xl">{profile.icon}</span>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-heading font-bold text-app-text truncate">
                {profile.name}
              </h1>
              <MarkdownText text={profile.description || "No description"} className="text-body text-app-text-muted mt-0.5" />
            </div>
          </div>
        </div>
      </Card>

      {/* Encryption Key Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Encryption Key
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {encKey ? (
            <>
              {/* Key exists — show it */}
              <div className="flex items-center gap-2 p-3 rounded-lg bg-app-surface-raised border border-app-border">
                <ShieldCheck className="w-5 h-5 text-app-success shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-app-text">Key is set</p>
                  {showKey ? (
                    <code className="text-xs font-mono text-app-text-muted break-all select-all">
                      {encKey}
                    </code>
                  ) : (
                    <p className="text-xs text-app-text-muted">
                      ••••••••••••••••••••••••
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowKey(!showKey)}
                >
                  {showKey ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </Button>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleClearKey}
                className="w-full text-app-danger hover:text-app-danger"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Key
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-app-text-muted">
                Import your encryption key to enable end-to-end encrypted
                messages. Get the key from your admin — paste it below or scan a
                QR code.
              </p>

              {/* Text import */}
              <div className="space-y-2">
                <Label htmlFor="key-input">Paste Encryption Key</Label>
                <div className="flex gap-2">
                  <Input
                    id="key-input"
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    placeholder="Paste base64 key here..."
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    onClick={handleImportKey}
                    disabled={!keyInput.trim()}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-app-border" />
                <span className="text-xs text-app-text-muted">or</span>
                <div className="flex-1 h-px bg-app-border" />
              </div>

              {/* QR Scanner */}
              {showScanner ? (
                <QRScanner
                  onScan={handleScanResult}
                  onClose={() => setShowScanner(false)}
                />
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowScanner(true)}
                  className="w-full"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Scan QR Code
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Change Passcode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Change Passcode
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePasscode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client-current-pass">Current Passcode</Label>
              <Input
                id="client-current-pass"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={currentPasscode}
                onChange={(e) => setCurrentPasscode(e.target.value)}
                placeholder="Enter current passcode"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-new-pass">New Passcode</Label>
              <Input
                id="client-new-pass"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={newPasscode}
                onChange={(e) => setNewPasscode(e.target.value)}
                placeholder="At least 4 digits"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-confirm-pass">Confirm New Passcode</Label>
              <Input
                id="client-confirm-pass"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={confirmPasscode}
                onChange={(e) => setConfirmPasscode(e.target.value)}
                placeholder="Re-enter new passcode"
              />
            </div>
            <Button
              type="submit"
              disabled={passcodeLoading || !currentPasscode || !newPasscode || !confirmPasscode}
              className="w-full"
            >
              {passcodeLoading ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner /> Changing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <KeyRound className="w-4 h-4" /> Change Passcode
                </span>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Holiday Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palmtree className="w-5 h-5" />
            Holiday Mode
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Admin-set holiday — read only */}
          {profile?.adminHoliday && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <span className="text-2xl">🏖️</span>
              <div className="flex-1">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  Holiday mode is active
                </p>
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Set by your admin.
                  {profile.adminEndsAt && (
                    <> Ends {new Date(profile.adminEndsAt).toLocaleDateString()}</>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Client-set holiday active */}
          {!profile?.adminHoliday && profile?.active && profile?.startedBy === "client" && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <span className="text-2xl">🏖️</span>
              <div className="flex-1">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  Holiday mode is active
                </p>
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {profile.endsAt && (
                    <>Ends {new Date(profile.endsAt).toLocaleDateString()}</>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Pending request */}
          {profile?.request?.status === "pending" && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
              <span className="text-2xl">⏳</span>
              <div className="flex-1">
                <p className="font-medium text-blue-800 dark:text-blue-200">
                  Holiday request pending
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  {new Date(profile.request.starts_at).toLocaleDateString()} → {new Date(profile.request.ends_at).toLocaleDateString()}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="text-app-danger hover:text-app-danger"
                onClick={handleCancelRequest}
                disabled={holidayLoading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Denied request */}
          {profile?.request?.status === "denied" && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
              <span className="text-2xl">❌</span>
              <div className="flex-1">
                <p className="font-medium text-red-800 dark:text-red-200">
                  Holiday request denied
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  You can submit a new request.
                </p>
              </div>
            </div>
          )}

          {/* Request form — show when no active holiday and no pending request */}
          {!profile?.active && profile?.request?.status !== "pending" && (
            <>
              <p className="text-sm text-app-text-muted">
                Request holiday mode to pause all chores and rules. Your admin needs to approve the request.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="holiday-start">Start Date</Label>
                  <Input
                    id="holiday-start"
                    type="date"
                    value={holidayStartDate}
                    onChange={(e) => setHolidayStartDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="holiday-end">End Date</Label>
                  <Input
                    id="holiday-end"
                    type="date"
                    value={holidayEndDate}
                    onChange={(e) => setHolidayEndDate(e.target.value)}
                    min={holidayStartDate || new Date().toISOString().split("T")[0]}
                  />
                </div>
              </div>
              <Button
                onClick={handleRequestHoliday}
                disabled={holidayLoading || !holidayStartDate || !holidayEndDate}
                className="w-full"
              >
                {holidayLoading ? (
                  <span className="flex items-center gap-2"><LoadingSpinner /> Submitting...</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Request Holiday
                  </span>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {notificationStatus === "unsupported" ? (
            <p className="text-sm text-app-text-muted">
              Push notifications are not supported in your browser.
            </p>
          ) : notificationStatus === "granted" ? (
            <>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <span className="text-2xl">🔔</span>
                <div className="flex-1">
                  <p className="font-medium text-green-800 dark:text-green-200">
                    Notifications enabled
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    You'll receive alerts for chores, grace warnings, daily briefings, and messages.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => handleEnableNotifications(true)}
                disabled={notificationLoading}
                variant="outline"
                className="w-full"
              >
                {notificationLoading ? (
                  <span className="flex items-center gap-2"><LoadingSpinner /> Re-registering...</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Bell className="w-4 h-4" /> Re-register Notifications
                  </span>
                )}
              </Button>
            </>
          ) : notificationStatus === "denied" ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
              <span className="text-2xl">🔕</span>
              <div className="flex-1">
                <p className="font-medium text-red-800 dark:text-red-200">
                  Notifications blocked
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Enable notifications in your browser settings to receive alerts.
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-app-text-muted">
                Enable push notifications to get instant alerts when chores unlock, grace periods end, and receive your daily briefing.
              </p>
              <Button
                onClick={() => handleEnableNotifications()}
                disabled={notificationLoading}
                className="w-full"
              >
                {notificationLoading ? (
                  <span className="flex items-center gap-2"><LoadingSpinner /> Enabling...</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Bell className="w-4 h-4" /> Enable Notifications
                  </span>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
